import { CREATOR_EVENT_CLICK, CREATOR_EVENT_DOWN, CREATOR_EVENT_MOVE, CREATOR_EVENT_UP, } from '../AnnotationCreator';
import { AnnotationSchemaV3 } from './AnnotationSchemaV3';
const HANDLE_TYPE_MOVE = 'move';
const HANDLE_TYPE_RESIZE = 'resize';
const HANDLE_POSITION_LEFT = 'left';
const HANDLE_POSITION_RIGHT = 'right';
const HANDLE_COLOR = 'yellow';
const HANDLE_DIMENSION = 15;
const MIN_WIDTH = 25;
const TIMEOUT_FOCUS = 50;
const RATIO_BASE = 1000;
const KEY_RETURN = 13;
const KEY_TAB = 9;
export class TextAnnotationComponent {
    constructor(rect, size, color, creator) {
        this.creator = creator;
        this.id = null;
        this.ratio = creator.zoomRatio; // ratio of the canvas vs the real element
        this.size = null;
        this.color = null;
        this.realRect = {};
        this.rect = {};
        this._visibleIndividual = true;
        this._visibleAll = true;
        this._dragging = false;
        this._created = false;
        this._editing = false;
        this._fixedDimensions = {}; // keeps track of manual dimensions / bounding borders
        this.setRect(rect);
        this.setColor(color);
        this.setSize(size);
        this.parent = null;
        this.element = null;
        this.repositionHandleLeft = null;
        this.resizeHandleRight = null;
        // Bound callbacks
        this._boundTextboxBlur = null;
        this._boundEditableKeyUp = null;
        this._boundEditableKeyDown = null;
        this._boundResizeLeftMouseDown = null;
        this._boundResizeLeftMouseUp = null;
        this._boundResizeLeftMouseMove = null;
        this._boundResizeRightMouseDown = null;
        this._boundResizeRightMouseUp = null;
        this._boundResizeRightMouseMove = null;
    }
    /**
     * Hide the textbox
     * @param {boolean} individual
     */
    hide(individual = true) {
        if (individual) {
            this._visibleIndividual = false;
        }
        else {
            this._visibleAll = false;
        }
        this._updateProperties();
    }
    /**
     * Show the textbox
     * @param {boolean} individual
     */
    show(individual) {
        if (individual) {
            this._visibleIndividual = true;
        }
        else {
            this._visibleAll = true;
        }
        if (this.isVisible()) {
            this.element.style.display = 'block'; // do this or the newly calculated rect will be off
        }
        this._updateProperties();
    }
    isVisible() {
        return this._visibleIndividual && this._visibleAll;
    }
    /**
     * Setter for rect
     * @param rect
     */
    setRect(rect) {
        this.rect = rect;
        this.realRect = {};
        for (const index in rect) {
            if (rect.hasOwnProperty(index)) {
                this.realRect[index] = rect[index] / this.ratio;
            }
        }
    }
    /**
     * Setter for size
     * @param {number} size
     */
    setSize(size) {
        this.size = parseFloat(size);
        if (this.element) {
            this._updateProperties();
        }
    }
    /**
     * Getter for size
     * @returns {number}
     */
    getSize() {
        return this.size;
    }
    /**
     * Set ratio
     * @param {number} ratio
     */
    setRatio(ratio) {
        if (this.ratio !== ratio) {
            this.ratio = ratio;
            this.rect.top = this.realRect.top * ratio;
            this.rect.left = this.realRect.left * ratio;
            this.rect.width = this.realRect.width * ratio;
            this.rect.height = this.realRect.height * ratio;
            if (this._fixedDimensions.width) {
                this._fixedDimensions.width =
                    this._fixedDimensions.realWidth * ratio;
            }
            // Max width will change, so delete this
            if (this._fixedDimensions.widthByMax) {
                delete this._fixedDimensions.widthByMax;
            }
            // update without recalculate + update with recalculate
            this._updateProperties(true);
            this._updateProperties();
        }
    }
    /**
     * Setter for color
     * @param color {string}
     */
    setColor(color) {
        this.color = color;
        if (this.element) {
            this._updateProperties();
        }
    }
    /**
     * Getter for color
     * @param color {string}
     */
    getColor() {
        return this.color;
    }
    /**
     * Setter for focus
     */
    setFocus() {
        this.element.focus();
    }
    /**
     * Create the elements based on the configuration and add them to the DOM
     * @param parent
     * @param restoring - true - means we restoring texbox and we don't need to focus it and set active
     * @param value - contains value for restoring textbox
     */
    create(parent, restoring, value) {
        if (this._created) {
            throw new Error('This TextAnnotationComponent has already been created');
        }
        this.parent = parent;
        this.repositionHandleLeft = this._createHandle(HANDLE_TYPE_MOVE, HANDLE_POSITION_LEFT);
        this.resizeHandleRight = this._createHandle(HANDLE_TYPE_RESIZE, HANDLE_POSITION_RIGHT);
        this.element = this._createContentElement();
        this._editing = true;
        this._created = true;
        parent.appendChild(this.element);
        parent.appendChild(this.repositionHandleLeft);
        parent.appendChild(this.resizeHandleRight);
        // store the width before recalculating in _updateProperties, this is later used to set the textbox width if the user has cropped the textbox manually
        const storedWidth = this.rect.width;
        this._updateProperties();
        this._addEditingEventListeners();
        if (restoring) {
            this.element.innerHTML = value;
            this._updateProperties();
            // When the calculated width is greater than the width stored in the annotations data, the user has cropped the textbox manually
            // set _fixedDimensions to prevent automatically scaling the textbox
            if (storedWidth < this.rect.width) {
                this._fixedDimensions.width = storedWidth;
                this._fixedDimensions.realWidth = storedWidth / this.ratio;
                this._updateProperties();
            }
            return;
        }
        this._focusElement();
        this.creator.setTextboxActive(true);
    }
    /**
     * Get the data for annotation
     */
    getData() {
        const parentRect = this._getParentRect();
        const x1 = this.rect.left / parentRect.width;
        const y1 = this.rect.top / parentRect.height;
        const x2 = (this.rect.left + this.rect.width) / parentRect.width;
        const y2 = (this.rect.top + this.rect.height) / parentRect.height;
        return {
            id: this.id,
            type: AnnotationSchemaV3.TYPE_TEXT,
            text: this.getText(),
            color: this.color,
            size: this.size,
            points: [
                [x1, y1],
                [x2, y2],
            ],
        };
    }
    /**
     * Get the text content of the box
     * @returns {string}
     */
    getText() {
        return this.element.value;
    }
    /**
     * Remove DOM element
     */
    remove() {
        this.parent.removeChild(this.element);
    }
    /**
     * Finish the text annotation
     * remove if empty, set state to not editing, remove css
     */
    finish() {
        if (this._editing) {
            this._editing = false;
            this._updateProperties();
            this._removeEditingEventListeners();
            this._addFinishedEventListeners();
            this.creator.setTextboxActive(false);
            // Remove if empty
            if (this.getText() === '') {
                delete this.creator.textBoxMap[this.id];
                this.remove();
            }
        }
    }
    /**
     * Escape input to prevent xss
     * @param {string} value
     * @returns {string}
     * @private
     */
    _escapeHtml(value) {
        var div = document.createElement('div');
        div.appendChild(document.createTextNode(value));
        return div.innerHTML;
    }
    // SPW-8932: save the parent rectangle width & height to correctly return annotations when the view is destroyed
    _getParentRect() {
        const parentRect = this.parent.getBoundingClientRect();
        if (parentRect.width !== 0 && parentRect.height !== 0) {
            this.parentRect = parentRect;
        }
        return this.parentRect;
    }
    /**
     * Calculate the text box offsets
     * @returns {object}
     * @private
     */
    _calculateRect() {
        var self = this;
        var rect = {};
        var elementRect = this.element.getBoundingClientRect();
        const parentRect = this._getParentRect();
        const LINE_HEIGHT = 1.2;
        // Get real dimensions for text box
        const textDimensions = calculateTextDimensions(this.element.value, this.size, this.ratio, LINE_HEIGHT);
        let realWidth = textDimensions[0];
        let realHeight = textDimensions[1];
        // add some pixels for smooth growing, otherwise it's jumpy
        realWidth += this.size * this.ratio * RATIO_BASE;
        // Get max width for the text box using it's left position + width of the parent
        const maxWidth = getMaxWidth();
        // Get max height of the box
        const maxHeight = getMaxHeight();
        // If the width has been set by user / return, use this value, otherwise calculate it
        rect.width = getRectWidth();
        if (realHeight > maxHeight) {
            // Check if can grow on top
            if (parentRect.height - realHeight > 0) {
                rect.top = Math.round(parentRect.height - realHeight);
                rect.height = Math.round(realHeight);
            }
            else {
                rect.top = 0;
                rect.height = Math.round(parentRect.height);
            }
        }
        else {
            rect.top = Math.round(elementRect.top - parentRect.top);
            rect.height = Math.round(realHeight);
        }
        rect.left = Math.round(elementRect.left - parentRect.left);
        // Prevent firefox moving box when not dragging
        if (!this._dragging && rect.top === this.rect.top + 1) {
            rect.top = this.rect.top;
        }
        return rect;
        function calculateTextDimensions(text, size, ratio, lineHeight) {
            const tmpWidth = self._createContentElement(true);
            const tmpHeight = self._createContentElement(true);
            tmpWidth.innerHTML = self._escapeHtml(text).replace(/\n/g, '<br>');
            // Need an extra char for new lines to be calculated
            tmpHeight.innerHTML =
                self._escapeHtml(text).replace(/\n/g, '<br>') + '|';
            if (self._fixedDimensions.width) {
                tmpWidth.style.width = self._fixedDimensions.width;
                tmpWidth.style.display = 'block';
                tmpHeight.style.width = self._fixedDimensions.width;
                tmpHeight.style.display = 'block';
            }
            else {
                tmpWidth.style.width = 'auto';
                tmpWidth.style.display = 'inline';
                tmpHeight.style.width = 'auto';
                tmpHeight.style.display = 'inline';
            }
            document.body.appendChild(tmpWidth);
            document.body.appendChild(tmpHeight);
            const rectWidth = tmpWidth.getBoundingClientRect();
            const rectHeight = tmpHeight.getBoundingClientRect();
            const parentRect = self._getParentRect();
            const width = Math.round(rectWidth.width);
            const height = Math.max(size * ratio * lineHeight * RATIO_BASE, Math.round(rectHeight.height));
            document.body.removeChild(tmpWidth);
            document.body.removeChild(tmpHeight);
            return [width, height];
        }
        function getMaxWidth() {
            const buffer = 20;
            return (parentRect.left + parentRect.width - elementRect.left - buffer);
        }
        function getMaxHeight() {
            return parentRect.top + parentRect.height - elementRect.top;
        }
        function getRectWidth() {
            let rectWidth = null;
            if (self._fixedDimensions.width) {
                rectWidth = self._fixedDimensions.width;
            }
            else if (self._fixedDimensions.widthByMax) {
                rectWidth = self._fixedDimensions.widthByMax;
            }
            else if (realWidth > maxWidth) {
                rectWidth = Math.round(maxWidth);
                // needed for next real dimension calculation // needs to be reset on ratio change
                self._fixedDimensions.widthByMax = maxWidth;
            }
            else {
                rectWidth = Math.round(Math.min(maxWidth, Math.max(MIN_WIDTH, realWidth)));
            }
            return rectWidth;
        }
    }
    /**
     * Update the positions and dimensions of all elements
     * @private
     */
    _updateProperties(skipCalculate = false) {
        if (!skipCalculate && this.isVisible()) {
            this.rect = this._calculateRect();
            this.realRect.width = this.rect.width / this.ratio;
            this.realRect.left = this.rect.left / this.ratio;
            this.realRect.top = this.rect.top / this.ratio;
            this.realRect.height = this.rect.height / this.ratio;
        }
        this.element.style.top = this.rect.top + 'px';
        this.element.style.left = this.rect.left + 'px';
        this.element.style.width = this.rect.width + 'px';
        this.element.style.height = this.rect.height + 'px';
        this.element.style.color = '#' + this.color;
        this.element.style.fontSize =
            this.size * this.ratio * RATIO_BASE + 'px';
        this.element.style.background = 'rgba(255, 255, 255, 0.25)';
        this.element.contentEditable = this._editing;
        if (this._editing && this.isVisible()) {
            this.element.style.display = 'block';
            this.element.style.borderLeftColor = HANDLE_COLOR;
            this.element.style.borderRightColor =
                this.element.style.borderLeftColor;
            this.repositionHandleLeft.style.display = 'block';
            this.repositionHandleLeft.style.top =
                this.rect.top +
                    Math.round((this.rect.height - HANDLE_DIMENSION) / 2) +
                    'px';
            this.repositionHandleLeft.style.left =
                this.rect.left - Math.round(HANDLE_DIMENSION / 2) + 'px';
            this.resizeHandleRight.style.display = 'block';
            this.resizeHandleRight.style.top =
                this.repositionHandleLeft.style.top;
            this.resizeHandleRight.style.left =
                this.rect.left + this.rect.width + 'px';
        }
        else if (this.isVisible()) {
            this.element.style.display = 'block';
            this.element.style.borderLeftColor = 'transparent';
            this.element.style.borderRightColor =
                this.element.style.borderLeftColor;
            this.repositionHandleLeft.style.display = 'none';
            this.resizeHandleRight.style.display = 'none';
        }
        else {
            this.element.style.display = 'none';
            this.repositionHandleLeft.style.display = 'none';
            this.resizeHandleRight.style.display = 'none';
        }
    }
    /**
     * Add event listeners to the elements
     * @private
     */
    _addEditingEventListeners() {
        if (!this._created) {
            throw new Error('Can not add event listeners when the elements have not been created');
        }
        let startX = 0;
        let startY = 0;
        /**
         * Callback when a key has been pressed in the editable div
         */
        function editableKeyUp(event) {
            // on pressing return, set fixed width
            if (event.which === KEY_RETURN) {
                this._fixedDimensions.width = this.rect.width;
                this._fixedDimensions.realWidth = this.rect.width / this.ratio;
            }
            this._updateProperties();
        }
        /**
         * Callback when a keydown event is fired in the editable div
         * @param event
         */
        function editableKeyDown(event) {
            event.stopPropagation(); // SP-36492
            if (event.which === KEY_TAB) {
                if (event.shiftKey) {
                    this.creator.focusPreviousTextBox(this.id);
                }
                else {
                    this.creator.focusNextTextBox(this.id);
                }
                event.preventDefault();
            }
        }
        /**
         * Callback when a mouse button is held down on the left resize handle
         * @param event
         */
        function moveLeftMouseDown(event) {
            //preventing focus lost during moving/resizing
            event.preventDefault();
            if (event.button === 0) {
                this._dragging = true;
                document.addEventListener(CREATOR_EVENT_MOVE, this._boundResizeLeftMouseMove);
                document.addEventListener(CREATOR_EVENT_UP, this._boundResizeLeftMouseUp);
                startX = event.clientX;
                startY = event.clientY;
            }
        }
        /**
         * Callback when a mouse button is released on the left resize handle
         * @param event
         */
        function moveLeftMouseUp(event) {
            if (event.button === 0) {
                this.dragging = false;
                document.removeEventListener(CREATOR_EVENT_MOVE, this._boundResizeLeftMouseMove);
                document.removeEventListener(CREATOR_EVENT_UP, this._boundResizeLeftMouseUp);
            }
        }
        /**
         * Callback when the cursor is moved the left resize handle
         * @param event
         */
        function moveLeftMouseMove(event) {
            var diffX = startX - event.clientX;
            var diffY = startY - event.clientY;
            startX = event.clientX;
            startY = event.clientY;
            const parentRect = this._getParentRect();
            const halfHandleDimension = HANDLE_DIMENSION / 2;
            const widthMinHalf = parentRect.width - halfHandleDimension;
            const heightMinHalf = parentRect.height - halfHandleDimension;
            if (this.rect.left - diffX > halfHandleDimension &&
                this.rect.left - diffX + this.rect.width < widthMinHalf) {
                this.element.style.left = this.rect.left - diffX + 'px';
            }
            if (this.rect.top - diffY > halfHandleDimension &&
                this.rect.top -
                    diffY +
                    this.element.getBoundingClientRect().height <
                    heightMinHalf) {
                this.element.style.top = this.rect.top - diffY + 'px';
            }
            this._updateProperties();
        }
        /**
         * Callback when a mouse button is held down on the right resize handle
         * @param event
         */
        function resizeRightMouseDown(event) {
            //preventing focus lost during moving/resizing
            event.preventDefault();
            if (event.button === 0) {
                document.addEventListener(CREATOR_EVENT_MOVE, this._boundResizeRightMouseMove);
                document.addEventListener(CREATOR_EVENT_UP, this._boundResizeRightMouseUp);
                startX = event.clientX;
                startY = event.clientY;
            }
        }
        /**
         * Callback when a mouse button is released on the right resize handle
         * @param event
         */
        function resizeRightMouseUp(event) {
            if (event.button === 0) {
                document.removeEventListener(CREATOR_EVENT_MOVE, this._boundResizeRightMouseMove);
                document.removeEventListener(CREATOR_EVENT_UP, this._boundResizeRightMouseUp);
            }
        }
        /**
         * Callback when the cursor is moved the right resize handle
         * @param event
         */
        function resizeRightMouseMove(event) {
            const diffX = event.clientX - startX;
            const buffer = 10;
            const parentRect = this._getParentRect();
            startX = event.clientX;
            startY = event.clientY;
            if (this.rect.width + diffX > MIN_WIDTH &&
                this.rect.left + this.rect.width + diffX <
                    parentRect.width - buffer) {
                this._fixedDimensions.width = this.rect.width + diffX;
                this._fixedDimensions.realWidth =
                    this._fixedDimensions.width / this.ratio;
                this._updateProperties();
            }
        }
        /**
         * Callback when textbox is blurred
         */
        function textboxBlur() {
            this.creator.setTextboxActive(false);
        }
        this._boundEditableKeyUp = editableKeyUp.bind(this);
        this._boundEditableKeyDown = editableKeyDown.bind(this);
        this._boundResizeLeftMouseDown = moveLeftMouseDown.bind(this);
        this._boundResizeLeftMouseUp = moveLeftMouseUp.bind(this);
        this._boundResizeLeftMouseMove = moveLeftMouseMove.bind(this);
        this._boundResizeRightMouseDown = resizeRightMouseDown.bind(this);
        this._boundResizeRightMouseUp = resizeRightMouseUp.bind(this);
        this._boundResizeRightMouseMove = resizeRightMouseMove.bind(this);
        this._boundTextboxBlur = textboxBlur.bind(this);
        this.element.addEventListener('blur', this._boundTextboxBlur);
        this.element.addEventListener('keyup', this._boundEditableKeyUp);
        this.element.addEventListener('keydown', this._boundEditableKeyDown);
        this.repositionHandleLeft.addEventListener(CREATOR_EVENT_DOWN, this._boundResizeLeftMouseDown);
        this.resizeHandleRight.addEventListener(CREATOR_EVENT_DOWN, this._boundResizeRightMouseDown);
    }
    /**
     * Remove event listeners from edit mode
     * @private
     */
    _removeEditingEventListeners() {
        this.element.removeEventListener('blur', this._boundTextboxBlur);
        this.element.removeEventListener('keyup', this._boundEditableKeyUp);
        this.element.removeEventListener('keydown', this._boundEditableKeyDown);
        this.repositionHandleLeft.removeEventListener(CREATOR_EVENT_DOWN, this._boundResizeLeftMouseDown);
        this.resizeHandleRight.removeEventListener(CREATOR_EVENT_DOWN, this._boundResizeRightMouseDown);
    }
    /**
     * Add event listeners after text annotation was finished (to restart editing)
     * @private
     */
    _addFinishedEventListeners() {
        /**
         * Callback when the finished text annotation element got clicked
         * @param event
         */
        function closedTextAnnotationClick(event) {
            // finish all text creations
            this.creator.finishTextAnnotations([this.id]);
            if (!this.creator.getIsEnabled()) {
                this.element.blur(); // remove focus, using disabled attribute disables the click as well
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            this._editing = true;
            this.creator.setTextboxActive(true);
            this._removeFinishedEventListeners();
            this._updateProperties();
            this._addEditingEventListeners();
            this._focusElement();
        }
        /**
         * On mousedown on the finished text annotation element
         * Used to kill the click early, if needed
         * @param event
         */
        function closedTextAnnotationMouseDown(event) {
            if (!this.creator.getIsEnabled()) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
        /**
         * On mousemove over the finished text annotations
         * Used to be able do draw non-text annotations behind the element
         * @param event
         */
        function closedTextAnnotionMouseMove(event) {
            if (this.creator.isCreatingAnnotation()) {
                this.creator.addAnnotationPoint(event, true);
            }
        }
        // Store bound callbacks
        this._boundClosedTextAnnotationClick =
            closedTextAnnotationClick.bind(this);
        this._boundClosedTextAnnotationMouseDown =
            closedTextAnnotationMouseDown.bind(this);
        this._boundClosedTextAnnotationMouseMove =
            closedTextAnnotionMouseMove.bind(this);
        // Add listeners
        this.element.addEventListener(CREATOR_EVENT_MOVE, this._boundClosedTextAnnotationMouseMove);
        this.element.addEventListener(CREATOR_EVENT_DOWN, this._boundClosedTextAnnotationMouseDown);
        this.element.addEventListener(CREATOR_EVENT_CLICK, this._boundClosedTextAnnotationClick);
    }
    /**
     * Remove the event listeners added after finish
     * @private
     */
    _removeFinishedEventListeners() {
        this.element.removeEventListener(CREATOR_EVENT_MOVE, this._boundClosedTextAnnotationMouseMove);
        this.element.removeEventListener(CREATOR_EVENT_DOWN, this._boundClosedTextAnnotationMouseDown);
        this.element.removeEventListener(CREATOR_EVENT_CLICK, this._boundClosedTextAnnotationClick);
    }
    /**
     * Focus the text box element, with timeout (needed for old IE)
     * @private
     */
    _focusElement() {
        // Give browser time to breath
        window.setTimeout(() => this.element.focus(), TIMEOUT_FOCUS);
    }
    /**
     * Create a content editable element
     * @param {boolean|undefined} hidden
     * @returns {HTMLElement}
     * @private
     */
    _createContentElement(hidden = false) {
        let elementType = null;
        if (typeof hidden !== 'boolean') {
            hidden = false;
        }
        if (hidden) {
            elementType = 'div';
        }
        else {
            elementType = 'textarea';
        }
        const element = document.createElement(elementType);
        element.style.fontFamily = AnnotationSchemaV3.TEXT_FONT_FAMILY;
        element.style.lineHeight = AnnotationSchemaV3.TEXT_LINE_HEIGHT;
        element.style.fontSize = this.size * this.ratio * RATIO_BASE + 'px';
        element.style.wordBreak = 'normal';
        element.style.wordWrap = 'break-word';
        element.style.position = 'absolute';
        element.style.boxSizing = 'border-box';
        element.style.setProperty('box-sizing', 'border-box', 'important'); // necessary due to crocodoc.css
        element.style.outline = 'none';
        element.style.boxShadow = 'none';
        element.style.borderTop = 'none';
        element.style.borderBottom = 'none';
        element.style.borderLeft = '2px solid ' + HANDLE_COLOR;
        element.style.borderRight = '2px solid ' + HANDLE_COLOR;
        element.style.padding = 0;
        element.style.margin = 0;
        element.autocomplete = 'off';
        element.autocorrect = 'off';
        element.autocapitalize = 'off';
        element.spellcheck = false;
        // start position/dimension
        element.style.top = this.rect.top + 'px';
        element.style.left = this.rect.left + 'px';
        element.style.width = this.rect.width + 'px';
        element.style.minHeight = 0;
        element.style.minWidth = 0;
        element.style.overflow = 'hidden';
        if (hidden) {
            element.style.visibility = 'invisible';
        }
        else {
            element.cols = 1;
            element.rows = 1;
            element.style.resize = 'none';
        }
        // Delete tabindex, this due some issues with AssetViewer
        // when tabbing, it tries to focus on elements on other
        // pages if there are multiple.
        element.tabIndex = -1;
        return element;
    }
    /**
     * Create a resize handle
     * @param type HANDLE_TYPE_MOVE or HANDLE_TYPE_RESIZE
     * @param position HANDLE_POSITION_LEFT or HANDLE_POSITION_RIGHT
     * @returns {HTMLElement}
     * @private
     */
    _createHandle(type, position) {
        if ([HANDLE_TYPE_MOVE, HANDLE_TYPE_RESIZE].indexOf(type) < 0) {
            throw new Error('Invalid handle type');
        }
        if ([HANDLE_POSITION_LEFT, HANDLE_POSITION_RIGHT].indexOf(position) < 0) {
            throw new Error('Invalid position type');
        }
        const handle = document.createElement('div');
        const handleBorderRadius = getBorderRadius(position);
        const handleCursorType = getCursorType(type);
        handle.style.position = 'absolute';
        handle.style.background = HANDLE_COLOR;
        handle.style.cursor = handleCursorType;
        handle.style.width = Math.round(HANDLE_DIMENSION / 2) + 'px';
        handle.style.height = `${HANDLE_DIMENSION}px`;
        handle.style.borderRadius = handleBorderRadius.join(' ');
        return handle;
        function getBorderRadius(handlePosition) {
            let borderRadius = null;
            if (handlePosition === HANDLE_POSITION_LEFT) {
                borderRadius = [
                    `${HANDLE_DIMENSION}px`,
                    0,
                    0,
                    `${HANDLE_DIMENSION}px`,
                ];
            }
            else {
                borderRadius = [
                    0,
                    `${HANDLE_DIMENSION}px`,
                    `${HANDLE_DIMENSION}px`,
                    0,
                ];
            }
            return borderRadius;
        }
        function getCursorType(cursorType) {
            let cursor = 'move';
            if (cursorType === 'resize') {
                cursor = 'ew-resize';
            }
            return cursor;
        }
    }
}
//# sourceMappingURL=TextAnnotationComponent.js.map